import { i18n } from "~vue/i18n";

// Card copy marks the lens names with <em>, which ICU MessageFormat parses as a rich-text tag;
// rendering it back as markup keeps the HTML the lens cards display unchanged.
const EMPHASIS = { em: (chunks: string[]) => `<em>${chunks.join("")}</em>` };

export interface Question {
    id: string;
    text: string;
    dimension: string;
    lens: string;
    direction: number;
    display_order: number;
}

export interface DimensionScore {
    /** Two-letter dimension code: EI, SN, TF, or JP. */
    code: Dimension;
    raw_score: number;
    /** Discretized score from -3 to +3. Negative = left letter dominant, positive = right. */
    binned_score: number;
    lenses: { lens: string; raw_score: number; binned_score: number; binned_label: string }[];
}

export interface PerspectiveResult {
    /** 4-letter type code, e.g. "INTJ". Each position maps to a dimension: E/I, S/N, T/F, J/P. */
    letters: string;
    archetype: string;
    dimensions: DimensionScore[];
}

export interface PerspectiveTypeTrait {
    title: string;
    body: string;
}

export interface PerspectiveType {
    title: string;
    shortDescription: string;
    intro: string;
    population: string;
    descriptions: string[];
    strengths: PerspectiveTypeTrait[];
    challenges: PerspectiveTypeTrait[];
}

export const PERSPECTIVE_TYPES: Record<string, PerspectiveType> = {
    INTJ: {
        get title() {
            return i18n.global.t("perspective.types.INTJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.INTJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.INTJ.intro");
        },
        population: "2.1%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.INTJ.descriptions.0"),
                i18n.global.t("perspective.types.INTJ.descriptions.1"),
                i18n.global.t("perspective.types.INTJ.descriptions.2"),
                i18n.global.t("perspective.types.INTJ.descriptions.3"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTJ.challenges.2.body");
                },
            },
        ],
    },
    INTP: {
        get title() {
            return i18n.global.t("perspective.types.INTP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.INTP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.INTP.intro");
        },
        population: "3.3%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.INTP.descriptions.0"),
                i18n.global.t("perspective.types.INTP.descriptions.1"),
                i18n.global.t("perspective.types.INTP.descriptions.2"),
                i18n.global.t("perspective.types.INTP.descriptions.3"),
                i18n.global.t("perspective.types.INTP.descriptions.4"),
                i18n.global.t("perspective.types.INTP.descriptions.5"),
                i18n.global.t("perspective.types.INTP.descriptions.6"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INTP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INTP.challenges.2.body");
                },
            },
        ],
    },
    ENTJ: {
        get title() {
            return i18n.global.t("perspective.types.ENTJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ENTJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ENTJ.intro");
        },
        population: "1.8%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ENTJ.descriptions.0"),
                i18n.global.t("perspective.types.ENTJ.descriptions.1"),
                i18n.global.t("perspective.types.ENTJ.descriptions.2"),
                i18n.global.t("perspective.types.ENTJ.descriptions.3"),
                i18n.global.t("perspective.types.ENTJ.descriptions.4"),
                i18n.global.t("perspective.types.ENTJ.descriptions.5"),
                i18n.global.t("perspective.types.ENTJ.descriptions.6"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTJ.challenges.2.body");
                },
            },
        ],
    },
    ENTP: {
        get title() {
            return i18n.global.t("perspective.types.ENTP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ENTP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ENTP.intro");
        },
        population: "3.2%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ENTP.descriptions.0"),
                i18n.global.t("perspective.types.ENTP.descriptions.1"),
                i18n.global.t("perspective.types.ENTP.descriptions.2"),
                i18n.global.t("perspective.types.ENTP.descriptions.3"),
                i18n.global.t("perspective.types.ENTP.descriptions.4"),
                i18n.global.t("perspective.types.ENTP.descriptions.5"),
                i18n.global.t("perspective.types.ENTP.descriptions.6"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENTP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENTP.challenges.2.body");
                },
            },
        ],
    },
    INFJ: {
        get title() {
            return i18n.global.t("perspective.types.INFJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.INFJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.INFJ.intro");
        },
        population: "1.5%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.INFJ.descriptions.0"),
                i18n.global.t("perspective.types.INFJ.descriptions.1"),
                i18n.global.t("perspective.types.INFJ.descriptions.2"),
                i18n.global.t("perspective.types.INFJ.descriptions.3"),
                i18n.global.t("perspective.types.INFJ.descriptions.4"),
                i18n.global.t("perspective.types.INFJ.descriptions.5"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFJ.challenges.2.body");
                },
            },
        ],
    },
    INFP: {
        get title() {
            return i18n.global.t("perspective.types.INFP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.INFP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.INFP.intro");
        },
        population: "4.4%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.INFP.descriptions.0"),
                i18n.global.t("perspective.types.INFP.descriptions.1"),
                i18n.global.t("perspective.types.INFP.descriptions.2"),
                i18n.global.t("perspective.types.INFP.descriptions.3"),
                i18n.global.t("perspective.types.INFP.descriptions.4"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.INFP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.INFP.challenges.2.body");
                },
            },
        ],
    },
    ENFJ: {
        get title() {
            return i18n.global.t("perspective.types.ENFJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ENFJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ENFJ.intro");
        },
        population: "2.5%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ENFJ.descriptions.0"),
                i18n.global.t("perspective.types.ENFJ.descriptions.1"),
                i18n.global.t("perspective.types.ENFJ.descriptions.2"),
                i18n.global.t("perspective.types.ENFJ.descriptions.3"),
                i18n.global.t("perspective.types.ENFJ.descriptions.4"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFJ.challenges.2.body");
                },
            },
        ],
    },
    ENFP: {
        get title() {
            return i18n.global.t("perspective.types.ENFP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ENFP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ENFP.intro");
        },
        population: "8.1%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ENFP.descriptions.0"),
                i18n.global.t("perspective.types.ENFP.descriptions.1"),
                i18n.global.t("perspective.types.ENFP.descriptions.2"),
                i18n.global.t("perspective.types.ENFP.descriptions.3"),
                i18n.global.t("perspective.types.ENFP.descriptions.4"),
                i18n.global.t("perspective.types.ENFP.descriptions.5"),
                i18n.global.t("perspective.types.ENFP.descriptions.6"),
                i18n.global.t("perspective.types.ENFP.descriptions.7"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ENFP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ENFP.challenges.2.body");
                },
            },
        ],
    },
    ISTJ: {
        get title() {
            return i18n.global.t("perspective.types.ISTJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ISTJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ISTJ.intro");
        },
        population: "11.6%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ISTJ.descriptions.0"),
                i18n.global.t("perspective.types.ISTJ.descriptions.1"),
                i18n.global.t("perspective.types.ISTJ.descriptions.2"),
                i18n.global.t("perspective.types.ISTJ.descriptions.3"),
                i18n.global.t("perspective.types.ISTJ.descriptions.4"),
                i18n.global.t("perspective.types.ISTJ.descriptions.5"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTJ.challenges.2.body");
                },
            },
        ],
    },
    ISFJ: {
        get title() {
            return i18n.global.t("perspective.types.ISFJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ISFJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ISFJ.intro");
        },
        population: "13.8%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ISFJ.descriptions.0"),
                i18n.global.t("perspective.types.ISFJ.descriptions.1"),
                i18n.global.t("perspective.types.ISFJ.descriptions.2"),
                i18n.global.t("perspective.types.ISFJ.descriptions.3"),
                i18n.global.t("perspective.types.ISFJ.descriptions.4"),
                i18n.global.t("perspective.types.ISFJ.descriptions.5"),
                i18n.global.t("perspective.types.ISFJ.descriptions.6"),
                i18n.global.t("perspective.types.ISFJ.descriptions.7"),
                i18n.global.t("perspective.types.ISFJ.descriptions.8"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFJ.challenges.2.body");
                },
            },
        ],
    },
    ESTJ: {
        get title() {
            return i18n.global.t("perspective.types.ESTJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ESTJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ESTJ.intro");
        },
        population: "8.7%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ESTJ.descriptions.0"),
                i18n.global.t("perspective.types.ESTJ.descriptions.1"),
                i18n.global.t("perspective.types.ESTJ.descriptions.2"),
                i18n.global.t("perspective.types.ESTJ.descriptions.3"),
                i18n.global.t("perspective.types.ESTJ.descriptions.4"),
                i18n.global.t("perspective.types.ESTJ.descriptions.5"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTJ.challenges.2.body");
                },
            },
        ],
    },
    ESFJ: {
        get title() {
            return i18n.global.t("perspective.types.ESFJ.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ESFJ.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ESFJ.intro");
        },
        population: "12.0%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ESFJ.descriptions.0"),
                i18n.global.t("perspective.types.ESFJ.descriptions.1"),
                i18n.global.t("perspective.types.ESFJ.descriptions.2"),
                i18n.global.t("perspective.types.ESFJ.descriptions.3"),
                i18n.global.t("perspective.types.ESFJ.descriptions.4"),
                i18n.global.t("perspective.types.ESFJ.descriptions.5"),
                i18n.global.t("perspective.types.ESFJ.descriptions.6"),
                i18n.global.t("perspective.types.ESFJ.descriptions.7"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFJ.challenges.2.body");
                },
            },
        ],
    },
    ISTP: {
        get title() {
            return i18n.global.t("perspective.types.ISTP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ISTP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ISTP.intro");
        },
        population: "5.4%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ISTP.descriptions.0"),
                i18n.global.t("perspective.types.ISTP.descriptions.1"),
                i18n.global.t("perspective.types.ISTP.descriptions.2"),
                i18n.global.t("perspective.types.ISTP.descriptions.3"),
                i18n.global.t("perspective.types.ISTP.descriptions.4"),
                i18n.global.t("perspective.types.ISTP.descriptions.5"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISTP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISTP.challenges.2.body");
                },
            },
        ],
    },
    ISFP: {
        get title() {
            return i18n.global.t("perspective.types.ISFP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ISFP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ISFP.intro");
        },
        population: "8.8%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ISFP.descriptions.0"),
                i18n.global.t("perspective.types.ISFP.descriptions.1"),
                i18n.global.t("perspective.types.ISFP.descriptions.2"),
                i18n.global.t("perspective.types.ISFP.descriptions.3"),
                i18n.global.t("perspective.types.ISFP.descriptions.4"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ISFP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ISFP.challenges.2.body");
                },
            },
        ],
    },
    ESTP: {
        get title() {
            return i18n.global.t("perspective.types.ESTP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ESTP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ESTP.intro");
        },
        population: "4.3%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ESTP.descriptions.0"),
                i18n.global.t("perspective.types.ESTP.descriptions.1"),
                i18n.global.t("perspective.types.ESTP.descriptions.2"),
                i18n.global.t("perspective.types.ESTP.descriptions.3"),
                i18n.global.t("perspective.types.ESTP.descriptions.4"),
                i18n.global.t("perspective.types.ESTP.descriptions.5"),
                i18n.global.t("perspective.types.ESTP.descriptions.6"),
                i18n.global.t("perspective.types.ESTP.descriptions.7"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESTP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESTP.challenges.2.body");
                },
            },
        ],
    },
    ESFP: {
        get title() {
            return i18n.global.t("perspective.types.ESFP.title");
        },
        get shortDescription() {
            return i18n.global.t("perspective.types.ESFP.shortDescription");
        },
        get intro() {
            return i18n.global.t("perspective.types.ESFP.intro");
        },
        population: "8.5%",
        get descriptions() {
            return [
                i18n.global.t("perspective.types.ESFP.descriptions.0"),
                i18n.global.t("perspective.types.ESFP.descriptions.1"),
                i18n.global.t("perspective.types.ESFP.descriptions.2"),
                i18n.global.t("perspective.types.ESFP.descriptions.3"),
                i18n.global.t("perspective.types.ESFP.descriptions.4"),
                i18n.global.t("perspective.types.ESFP.descriptions.5"),
                i18n.global.t("perspective.types.ESFP.descriptions.6"),
            ];
        },
        strengths: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.strengths.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.strengths.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.strengths.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.strengths.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.strengths.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.strengths.2.body");
                },
            },
        ],
        challenges: [
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.challenges.0.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.challenges.0.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.challenges.1.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.challenges.1.body");
                },
            },
            {
                get title() {
                    return i18n.global.t("perspective.types.ESFP.challenges.2.title");
                },
                get body() {
                    return i18n.global.t("perspective.types.ESFP.challenges.2.body");
                },
            },
        ],
    },
};

/** Two-letter dimension code: EI, SN, TF, or JP. */
export type Dimension = "EI" | "SN" | "TF" | "JP";

export const DIMENSION_LABELS: Record<Dimension, { left: string; right: string }> = {
    EI: {
        get left() {
            return i18n.global.t("perspective.types.dimensionLabels.EI.left");
        },
        get right() {
            return i18n.global.t("perspective.types.dimensionLabels.EI.right");
        },
    },
    SN: {
        get left() {
            return i18n.global.t("perspective.types.dimensionLabels.SN.left");
        },
        get right() {
            return i18n.global.t("perspective.types.dimensionLabels.SN.right");
        },
    },
    TF: {
        get left() {
            return i18n.global.t("perspective.types.dimensionLabels.TF.left");
        },
        get right() {
            return i18n.global.t("perspective.types.dimensionLabels.TF.right");
        },
    },
    JP: {
        get left() {
            return i18n.global.t("perspective.types.dimensionLabels.JP.left");
        },
        get right() {
            return i18n.global.t("perspective.types.dimensionLabels.JP.right");
        },
    },
};

/** Per-dimension bar colors (Tailwind classes using CSS variables from tailwind.css). */
export const DIMENSION_COLORS: Record<Dimension, string> = {
    EI: "bg-perspective-dimension-ei",
    SN: "bg-perspective-dimension-sn",
    TF: "bg-perspective-dimension-tf",
    JP: "bg-perspective-dimension-jp",
};

/**
 * The archetype names the API sends, which are always English. `PERSPECTIVE_TYPES[code].title` follows the
 * UI language, so it cannot be used to map an API name back to its type code.
 * Keep in sync with `_ARCHETYPES` in coach_api/coach_api/perspective/service.py.
 */
export const API_ARCHETYPE_LETTERS: Record<string, string> = {
    Architect: "INTJ",
    Logician: "INTP",
    Commander: "ENTJ",
    Debater: "ENTP",
    Advocate: "INFJ",
    Mediator: "INFP",
    Protagonist: "ENFJ",
    Campaigner: "ENFP",
    Logistician: "ISTJ",
    Defender: "ISFJ",
    Executive: "ESTJ",
    Consul: "ESFJ",
    Virtuoso: "ISTP",
    Adventurer: "ISFP",
    Entrepreneur: "ESTP",
    Entertainer: "ESFP",
};

/** Canonical ordering of the 16 types around the perspective wheel. */
export const WHEEL_ORDER = ["ISTP", "ISFP", "ESTP", "ESFP", "ISTJ", "ISFJ", "ESTJ", "ESFJ", "ENFP", "ENFJ", "INFP", "INFJ", "INTJ", "INTP", "ENTJ", "ENTP"] as const;

export { TYPE_COLORS } from "~vue/utils/perspectivePalette";

export enum PerspectiveLens {
    // energy
    INITIATING_RECEIVING = "initiating-receiving",
    EXPRESSIVE_CONTAINED = "expressive-contained",
    GREGARIOUS_INTIMATE = "gregarious-intimate",
    ACTIVE_REFLECTIVE = "active-reflective",
    ENTHUSIASTIC_QUIET = "enthusiastic-quiet",

    // processing
    CONCRETE_ABSTRACT = "concrete-abstract",
    REALISTIC_IMAGINATIVE = "realistic-imaginative",
    PRACTICAL_CONCEPTUAL = "practical-conceptual",
    EXPERIENTIAL_THEORETICAL = "experiential-theoretical",
    TRADITIONAL_ORIGINAL = "traditional-original",

    // decisions
    LOGICAL_EMPATHETIC = "logical-empathetic",
    REASONABLE_COMPASSIONATE = "reasonable-compassionate",
    QUESTIONING_ACCOMMODATING = "questioning-accommodating",
    CRITICAL_ACCEPTING = "critical-accepting",
    TOUGH_TENDER = "tough-tender",

    // structure
    SYSTEMATIC_CASUAL = "systematic-casual",
    PLANFUL_OPEN_ENDED = "planful-openended",
    EARLY_STARTING_PRESSURE_PROMPTED = "earlystarting-pressureprompted",
    SCHEDULED_SPONTANEOUS = "scheduled-spontaneous",
    METHODICAL_EMERGENT = "methodical-emergent",
}

export type PerspectiveLensStrLiterals = `${PerspectiveLens}`;

export const DIMENSION_LETTER_TO_LENS: Record<Dimension, PerspectiveLens[]> = {
    EI: [
        PerspectiveLens.INITIATING_RECEIVING,
        PerspectiveLens.EXPRESSIVE_CONTAINED,
        PerspectiveLens.GREGARIOUS_INTIMATE,
        PerspectiveLens.ACTIVE_REFLECTIVE,
        PerspectiveLens.ENTHUSIASTIC_QUIET,
    ],
    SN: [
        PerspectiveLens.CONCRETE_ABSTRACT,
        PerspectiveLens.REALISTIC_IMAGINATIVE,
        PerspectiveLens.PRACTICAL_CONCEPTUAL,
        PerspectiveLens.EXPERIENTIAL_THEORETICAL,
        PerspectiveLens.TRADITIONAL_ORIGINAL,
    ],
    TF: [
        PerspectiveLens.LOGICAL_EMPATHETIC,
        PerspectiveLens.REASONABLE_COMPASSIONATE,
        PerspectiveLens.QUESTIONING_ACCOMMODATING,
        PerspectiveLens.CRITICAL_ACCEPTING,
        PerspectiveLens.TOUGH_TENDER,
    ],
    JP: [
        PerspectiveLens.SYSTEMATIC_CASUAL,
        PerspectiveLens.PLANFUL_OPEN_ENDED,
        PerspectiveLens.EARLY_STARTING_PRESSURE_PROMPTED,
        PerspectiveLens.SCHEDULED_SPONTANEOUS,
        PerspectiveLens.METHODICAL_EMERGENT,
    ],
};

export const PERSPECTIVE_PAIR_LENS_ANCHOR: Record<PerspectiveLens, string> = {
    // Energy
    get [PerspectiveLens.INITIATING_RECEIVING]() {
        return i18n.global.t("perspective.types.lenses.initiatingReceiving.anchor");
    },
    get [PerspectiveLens.EXPRESSIVE_CONTAINED]() {
        return i18n.global.t("perspective.types.lenses.expressiveContained.anchor");
    },
    get [PerspectiveLens.GREGARIOUS_INTIMATE]() {
        return i18n.global.t("perspective.types.lenses.gregariousIntimate.anchor");
    },
    get [PerspectiveLens.ACTIVE_REFLECTIVE]() {
        return i18n.global.t("perspective.types.lenses.activeReflective.anchor");
    },
    get [PerspectiveLens.ENTHUSIASTIC_QUIET]() {
        return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.anchor");
    },

    // Processing
    get [PerspectiveLens.CONCRETE_ABSTRACT]() {
        return i18n.global.t("perspective.types.lenses.concreteAbstract.anchor");
    },
    get [PerspectiveLens.REALISTIC_IMAGINATIVE]() {
        return i18n.global.t("perspective.types.lenses.realisticImaginative.anchor");
    },
    get [PerspectiveLens.PRACTICAL_CONCEPTUAL]() {
        return i18n.global.t("perspective.types.lenses.practicalConceptual.anchor");
    },
    get [PerspectiveLens.EXPERIENTIAL_THEORETICAL]() {
        return i18n.global.t("perspective.types.lenses.experientialTheoretical.anchor");
    },
    get [PerspectiveLens.TRADITIONAL_ORIGINAL]() {
        return i18n.global.t("perspective.types.lenses.traditionalOriginal.anchor");
    },

    // decisions
    get [PerspectiveLens.LOGICAL_EMPATHETIC]() {
        return i18n.global.t("perspective.types.lenses.logicalEmpathetic.anchor");
    },
    get [PerspectiveLens.REASONABLE_COMPASSIONATE]() {
        return i18n.global.t("perspective.types.lenses.reasonableCompassionate.anchor");
    },
    get [PerspectiveLens.QUESTIONING_ACCOMMODATING]() {
        return i18n.global.t("perspective.types.lenses.questioningAccommodating.anchor");
    },
    get [PerspectiveLens.CRITICAL_ACCEPTING]() {
        return i18n.global.t("perspective.types.lenses.criticalAccepting.anchor");
    },
    get [PerspectiveLens.TOUGH_TENDER]() {
        return i18n.global.t("perspective.types.lenses.toughTender.anchor");
    },

    // structure
    get [PerspectiveLens.SYSTEMATIC_CASUAL]() {
        return i18n.global.t("perspective.types.lenses.systematicCasual.anchor");
    },
    get [PerspectiveLens.PLANFUL_OPEN_ENDED]() {
        return i18n.global.t("perspective.types.lenses.planfulOpenEnded.anchor");
    },
    get [PerspectiveLens.EARLY_STARTING_PRESSURE_PROMPTED]() {
        return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.anchor");
    },
    get [PerspectiveLens.SCHEDULED_SPONTANEOUS]() {
        return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.anchor");
    },
    get [PerspectiveLens.METHODICAL_EMERGENT]() {
        return i18n.global.t("perspective.types.lenses.methodicalEmergent.anchor");
    },
};

export type PerspectiveLensCardData = {
    dimension: string;
    leftLabel: string;
    rightLabel: string;
    cardNegative: string;
    cardSlightlyNegative: string;
    cardNeutral: string;
    cardSlightlyPositive: string;
    cardPositive: string;
};

export const PERSPECTIVE_LENS_CARD_DATA: Record<PerspectiveLens, PerspectiveLensCardData> = {
    [PerspectiveLens.INITIATING_RECEIVING]: {
        dimension: "EI",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.initiatingReceiving.cardPositive");
        },
    },
    [PerspectiveLens.EXPRESSIVE_CONTAINED]: {
        dimension: "EI",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.expressiveContained.cardPositive");
        },
    },
    [PerspectiveLens.GREGARIOUS_INTIMATE]: {
        dimension: "EI",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.gregariousIntimate.cardPositive");
        },
    },
    [PerspectiveLens.ACTIVE_REFLECTIVE]: {
        dimension: "EI",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.activeReflective.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.activeReflective.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.activeReflective.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.activeReflective.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.activeReflective.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.activeReflective.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.activeReflective.cardPositive");
        },
    },
    [PerspectiveLens.ENTHUSIASTIC_QUIET]: {
        dimension: "EI",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.enthusiasticQuiet.cardPositive");
        },
    },
    [PerspectiveLens.CONCRETE_ABSTRACT]: {
        dimension: "SN",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.concreteAbstract.cardPositive");
        },
    },
    [PerspectiveLens.REALISTIC_IMAGINATIVE]: {
        dimension: "SN",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.realisticImaginative.cardPositive");
        },
    },
    [PerspectiveLens.PRACTICAL_CONCEPTUAL]: {
        dimension: "SN",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.practicalConceptual.cardPositive");
        },
    },
    [PerspectiveLens.EXPERIENTIAL_THEORETICAL]: {
        dimension: "SN",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.experientialTheoretical.cardPositive");
        },
    },
    [PerspectiveLens.TRADITIONAL_ORIGINAL]: {
        dimension: "SN",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.traditionalOriginal.cardPositive");
        },
    },
    [PerspectiveLens.LOGICAL_EMPATHETIC]: {
        dimension: "TF",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.logicalEmpathetic.cardPositive");
        },
    },
    [PerspectiveLens.REASONABLE_COMPASSIONATE]: {
        dimension: "TF",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.reasonableCompassionate.cardPositive");
        },
    },
    [PerspectiveLens.QUESTIONING_ACCOMMODATING]: {
        dimension: "TF",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.questioningAccommodating.cardPositive");
        },
    },
    [PerspectiveLens.CRITICAL_ACCEPTING]: {
        dimension: "TF",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.criticalAccepting.cardPositive");
        },
    },
    [PerspectiveLens.TOUGH_TENDER]: {
        dimension: "TF",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.toughTender.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.toughTender.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.toughTender.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.toughTender.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.toughTender.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.toughTender.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.toughTender.cardPositive");
        },
    },
    [PerspectiveLens.SYSTEMATIC_CASUAL]: {
        dimension: "JP",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.systematicCasual.cardPositive");
        },
    },
    [PerspectiveLens.PLANFUL_OPEN_ENDED]: {
        dimension: "JP",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.planfulOpenEnded.cardPositive");
        },
    },
    [PerspectiveLens.EARLY_STARTING_PRESSURE_PROMPTED]: {
        dimension: "JP",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.earlyStartingPressurePrompted.cardPositive");
        },
    },
    [PerspectiveLens.SCHEDULED_SPONTANEOUS]: {
        dimension: "JP",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.scheduledSpontaneous.cardPositive");
        },
    },
    [PerspectiveLens.METHODICAL_EMERGENT]: {
        dimension: "JP",
        get leftLabel() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.leftLabel");
        },
        get rightLabel() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.rightLabel");
        },
        get cardNegative() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.cardNegative");
        },
        get cardSlightlyNegative() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.cardSlightlyNegative", EMPHASIS);
        },
        get cardNeutral() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.cardNeutral", EMPHASIS);
        },
        get cardSlightlyPositive() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.cardSlightlyPositive", EMPHASIS);
        },
        get cardPositive() {
            return i18n.global.t("perspective.types.lenses.methodicalEmergent.cardPositive");
        },
    },
};

export type PerspectiveTopicCategory = "strength" | "growth_area";
